import { images } from '@/asset';
import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import TestEditor from '@/components/TestEditor';
import { optionLanguage } from '@/constants/options';
import useTranslate from '@/hooks/useTranslate';
import languageRuleSlice, { selectedLanguageRuleSelector } from '@/redux/slice/languageRule.slice';
import { rulesSelector } from '@/redux/slice/rules.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { ILanguageRuleContent } from '@/types/languageRule.type';
import { Badge, Banner, Button, TextField } from '@shopify/polaris';
import parse from 'html-react-parser';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../CustomTable';
import { EditContentStyled } from './styled';
import { LanguageTranslateIcon } from '@shopify/polaris-icons';

interface IDefaultDataItem {
  id: string;
  label: string;
  field: string;
  ruleName?: string;
  messageText?: string;
  tooltipText?: string;
  tooltipMore?: string;
  textInCart?: string;
  customTextInCart?: string;
  customSoldOut?: string;
}

interface IProps {
  ruleSelectedId: number;
  isAdded?: boolean;
}

const EditContent = ({ ruleSelectedId, isAdded }: IProps) => {
  const dispatch = useDispatch();
  const currentLanguageRule = useSelector(selectedLanguageRuleSelector);
  const { allRules } = useSelector(rulesSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const { translateText, isShowTranslate } = useTranslate();
  const ruleSelected = useMemo(() => {
    return currentLanguageRule.ruleContents.find((rule) => rule.id === ruleSelectedId);
  }, [currentLanguageRule.ruleContents, ruleSelectedId]);

  const [tempRuleSelected, setTempRuleSelected] = useState<ILanguageRuleContent | undefined>(ruleSelected);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!ruleSelectedId) return;
    setTempRuleSelected(ruleSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleSelectedId, JSON.stringify(ruleSelected)]);

  const defaultDataRule = useMemo(() => {
    return allRules.find((rule) => rule.id === ruleSelectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allRules), ruleSelectedId]);

  const isHasTooltip = useMemo(() => {
    return defaultDataRule?.privacyPageText || defaultDataRule?.privacyMore;
  }, [defaultDataRule?.privacyPageText, defaultDataRule?.privacyMore]);

  const isShowTextInCart = useMemo(() => {
    return defaultDataRule?.showTextInCart;
  }, [defaultDataRule?.showTextInCart]);

  const isShowForSoldOut = useMemo(() => {
    return defaultDataRule?.soldOutEnabled;
  }, [defaultDataRule?.soldOutEnabled]);

  const handleChangeValue = useCallback((field: keyof ILanguageRuleContent, value: string) => {
    setTempRuleSelected((prev) => ({ ...prev, [field]: value } as ILanguageRuleContent));
  }, []);

  const handleDispatchUpdateRuleSelected = useCallback(
    (newValues: Partial<ILanguageRuleContent>) => {
      if (!currentLanguageRule?.ruleContents?.length) return;
      const newRuleContents = currentLanguageRule.ruleContents.map((rule) => {
        if (rule.id === ruleSelectedId) {
          return { ...rule, ...newValues };
        }
        return rule;
      });
      dispatch(languageRuleSlice.actions.handleSetSelectedRule({ ...currentLanguageRule, ruleContents: newRuleContents }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(currentLanguageRule), dispatch, ruleSelectedId],
  );

  const handleUpdateRuleSelected = useCallback(() => {
    tempRuleSelected && handleDispatchUpdateRuleSelected(tempRuleSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDispatchUpdateRuleSelected, JSON.stringify(tempRuleSelected)]);

  const handleTranslate = async () => {
    if (!tempRuleSelected) return;
    const newRuleContent = { ...tempRuleSelected };
    if (defaultDataRule && tempRuleSelected) {
      setIsLoading(true);
      const promiseArr: { condition?: string | boolean; text: string; field: keyof ILanguageRuleContent }[] = [
        { condition: !newRuleContent.name, text: defaultDataRule.name, field: 'name' },
        { condition: !newRuleContent.estimatedText, text: defaultDataRule.estimatedText || '', field: 'estimatedText' },
        {
          condition: isHasTooltip && !newRuleContent.privacyPageText,
          text: defaultDataRule.privacyPageText || '',
          field: 'privacyPageText',
        },
        { condition: isHasTooltip && !newRuleContent.privacyMore, text: defaultDataRule.privacyMore || '', field: 'privacyMore' },
        {
          condition: isShowTextInCart && !newRuleContent.cartPageLabelText,
          text: defaultDataRule.cartPageLabelText || '',
          field: 'cartPageLabelText',
        },
        {
          condition: isShowTextInCart && !newRuleContent.cartPageDateDisplay,
          text: defaultDataRule.cartPageDateDisplay || '',
          field: 'cartPageDateDisplay',
        },
        {
          condition: isShowForSoldOut && !newRuleContent.soldOutCustomText,
          text: defaultDataRule.soldOutCustomText || '',
          field: 'soldOutCustomText',
        },
      ];
      await Promise.all(
        promiseArr.map(
          ({ condition, text, field }) =>
            !!condition && translateText(text).then((res) => field !== 'id' && (newRuleContent[field] = res)),
        ),
      );

      const newTempRuleSelected: ILanguageRuleContent = {
        id: tempRuleSelected?.id || 0,
        name: newRuleContent.name || '',
        estimatedText: newRuleContent.estimatedText || '',
        privacyPageText: newRuleContent.privacyPageText || '',
        privacyMore: newRuleContent.privacyMore || '',
        cartPageLabelText: newRuleContent.cartPageLabelText || '',
        cartPageDateDisplay: newRuleContent.cartPageDateDisplay || '',
        soldOutCustomText: newRuleContent.soldOutCustomText || '',
      };

      setTempRuleSelected(newTempRuleSelected);
      handleDispatchUpdateRuleSelected(newTempRuleSelected);
      setIsLoading(false);
    }
  };

  const defaultData: IDefaultDataItem[] = useMemo(() => {
    return [
      {
        id: '1',
        label: 'Rule name',
        ruleName: defaultDataRule?.name,
        field: 'name',
      },
      {
        id: '2',
        label: `Message text (do not change date variables)`,
        messageText: defaultDataRule?.estimatedText,
        field: 'message',
      },
      isHasTooltip && {
        id: '3',
        label: 'Tooltip',
        tooltipText: defaultDataRule?.privacyPageText || '_',
        tooltipMore: defaultDataRule?.privacyMore || '_',
        field: 'tooltip',
      },
      isShowTextInCart && {
        id: '4',
        label: 'Label text in cart',
        textInCart: defaultDataRule?.cartPageLabelText,
        field: 'textInCart',
      },
      isShowTextInCart && {
        id: '5',
        label: 'Customize text in cart',
        customTextInCart: defaultDataRule?.cartPageDateDisplay,
        field: 'customTextInCart',
      },
      isShowForSoldOut && {
        id: '6',
        label: 'Customize text when out of stock',
        customSoldOut: defaultDataRule?.soldOutCustomText,
        field: 'customSoldOut',
      },
    ].filter(Boolean) as IDefaultDataItem[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultDataRule), isHasTooltip, isShowTextInCart, isShowForSoldOut]);

  const actionCells = useMemo(() => {
    return {
      name: (
        <TestEditor
          value={tempRuleSelected?.name || ''}
          onChange={(_, styledValue) => {
            handleChangeValue('name', styledValue || '');
          }}
          onBlur={handleUpdateRuleSelected}
          disabled={!ruleSelected}
          onlyShowEmojiInIconButton
          isHideTipsAndTemplate
          isHideVariableButton
        />
      ),
      message: (
        <TestEditor
          value={tempRuleSelected?.estimatedText || ''}
          onChange={(_, styledValue) => {
            handleChangeValue('estimatedText', styledValue || '');
          }}
          onBlur={handleUpdateRuleSelected}
          disabled={!ruleSelected}
        />
      ),
      tooltip: (
        <div className="d-flex flex-column gap-12 mt-8">
          <TextField
            autoComplete="off"
            label={<BoldSmallText>Text</BoldSmallText>}
            value={tempRuleSelected?.privacyPageText}
            onChange={(value) => handleChangeValue('privacyPageText', value || '')}
            error={''}
            onBlur={handleUpdateRuleSelected}
            disabled={!ruleSelected}
          />
          <TextField
            autoComplete="off"
            label={<BoldSmallText>More info text</BoldSmallText>}
            value={tempRuleSelected?.privacyMore}
            onChange={(value) => handleChangeValue('privacyMore', value || '')}
            onBlur={handleUpdateRuleSelected}
            disabled={!ruleSelected}
          />
        </div>
      ),
      textInCart: (
        <TextField
          autoComplete="off"
          labelHidden
          label="Label text in cart"
          value={tempRuleSelected?.cartPageLabelText}
          onChange={(value) => handleChangeValue('cartPageLabelText', value || '')}
          onBlur={handleUpdateRuleSelected}
          disabled={!ruleSelected}
        />
      ),
      customTextInCart: (
        <TextField
          autoComplete="off"
          labelHidden
          label="Customize text in cart"
          value={tempRuleSelected?.cartPageDateDisplay}
          onChange={(value) => handleChangeValue('cartPageDateDisplay', value || '')}
          onBlur={handleUpdateRuleSelected}
          disabled={!ruleSelected}
        />
      ),
      customSoldOut: (
        <TestEditor
          value={tempRuleSelected?.soldOutCustomText || ''}
          onChange={(_, styledValue) => {
            handleChangeValue('soldOutCustomText', styledValue || '');
          }}
          onBlur={handleUpdateRuleSelected}
          disabled={!ruleSelected}
        />
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tempRuleSelected?.name,
    tempRuleSelected?.privacyPageText,
    tempRuleSelected?.privacyMore,
    tempRuleSelected?.cartPageLabelText,
    tempRuleSelected?.cartPageDateDisplay,
    handleUpdateRuleSelected,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(ruleSelected),
    ruleSelectedId,
    handleChangeValue,
  ]);

  const headings = [
    {
      title: 'Field',
      width: '15%',
    },
    {
      title: `Default language (${optionLanguage.find((option) => option.value === dataSettings?.date_locale)?.label})`,
      width: '30%',
    },
    {
      title: currentLanguageRule?.nameLanguage,
      width: '55%',
    },
  ];

  const rowMarkup = useMemo(() => {
    return defaultData.map(
      ({ label, field, ruleName, messageText, tooltipText, tooltipMore, textInCart, customTextInCart, customSoldOut }) =>
        [
          label || '',
          ruleName ? parse(ruleName || '') : null,
          messageText ? parse(messageText || '') : null,
          tooltipText || tooltipMore ? (
            <div className="d-flex flex-column">
              <BoldSmallText>Text</BoldSmallText>
              <RegularText>{tooltipText}</RegularText>
              <div className="mt-8 d-flex flex-column">
                <BoldSmallText>More info text</BoldSmallText>
                <RegularText>{tooltipMore}</RegularText>
              </div>
            </div>
          ) : null,
          textInCart || '',
          customTextInCart || '',
          customSoldOut ? parse(customSoldOut || '') : null,
          actionCells[field as keyof typeof actionCells] || '',
        ].filter((item) => !!item),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionCells, JSON.stringify(defaultData)]);

  return (
    <EditContentStyled className="d-flex flex-column gap-8">
      <div className="d-flex gap-8 justify-between">
        <div className="d-flex gap-8 align-center">
          <BoldText>{defaultDataRule?.name}</BoldText>
          {ruleSelectedId ? isAdded ? <Badge tone="success">Added</Badge> : <Badge tone="critical">Not added</Badge> : null}
        </div>
        {ruleSelectedId && isAdded && isShowTranslate ? (
          <Button icon={LanguageTranslateIcon} onClick={handleTranslate} loading={isLoading}>
            Auto-translate
          </Button>
        ) : null}
      </div>
      {ruleSelectedId && !ruleSelected ? (
        <div className="mb-4">
          <Banner tone="info">Add this rule to the language first to start editing</Banner>
        </div>
      ) : null}
      <div className="table-container" id="edit-content-language-table">
        {ruleSelectedId ? (
          <CustomTable headings={headings} rows={rowMarkup} />
        ) : (
          <div className="d-flex align-center content-center flex-column" style={{ minHeight: '70vh' }}>
            <img width={'35%'} src={images.noRuleSelected} alt="no-rule-selected" />
            <b style={{ fontSize: '16px', marginTop: '45px' }}>Add a rule to this language to start editing</b>
          </div>
        )}
      </div>
    </EditContentStyled>
  );
};

export default memo(EditContent);
